import { translateBlockValue } from 'i18n';
import { BudgetExcelExportCol } from 'routes/BudgetModule/helpers/export';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';

export const LINE_BREAK_REGEX = /(\r\n|\n|\r)/gm;

type CSVDataPoint<HeaderT extends string> = Record<
    HeaderT,
    string | number | Date
>;
type CSVData<HeaderT extends string> = CSVDataPoint<HeaderT>[];

export const dataToCSVString = <HeaderT extends string>(
    data: CSVData<HeaderT>,
    headers: readonly HeaderT[],
    cellPreProcess:
        | ((cellStr: string, headerKey: string) => string)
        | null = null,
) => {
    const separator = ';';
    const separatorRegex = /;/g;

    // we start with utf8 BOM prefixe to avoid encoding errors
    // https://stackoverflow.com/questions/17879198/adding-utf-8-bom-to-string-blob
    const utf8BOM = '\ufeff';

    let csvStr = `${utf8BOM}${headers.join(separator)}\n`;

    const serializeCell = (row: CSVDataPoint<HeaderT>, headerKey: HeaderT) => {
        const cellValue = headerKey in row ? row[headerKey] : ' ';
        const cellStr =
            cellPreProcess !== null
                ? cellPreProcess(cellValue.toString(), headerKey)
                : cellValue.toString();

        // we remove multiline values, double quotes and separators in the cell value
        return cellStr
            .replace(LINE_BREAK_REGEX, ' ')
            .replace(separatorRegex, ' ')
            .replace(/"/g, ' ');
    };

    data.forEach((row) => {
        const csvRowValues = headers.map((header) =>
            serializeCell(row, header),
        );
        csvStr += `${csvRowValues.join(separator)}\n`;
    });
    return csvStr;
};

/**
 * Generate a CSV file from a string and download it
 * @param csvStr the CSV content
 * @param title the file title
 */
export const downloadCSV = (csvStr: string, title: string): void => {
    const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = url;
    hiddenElement.target = '_blank';
    hiddenElement.download = `${title}.csv`;
    hiddenElement.click();

    // Revoke the blob URL to release memory
    URL.revokeObjectURL(url);
};

/** Given cols and data, create an excel sheet and write it as a file to download
 */
type ExcelT = {
    selectedColsToExport: BudgetExcelExportCol[];
    selectedDataToExport: InscriptionT[];
    filename: string;
    origin: string;
    sheetName: string | undefined;
    extraSheets: string[] | undefined;
    extraRows: (string | undefined | number)[][] | undefined;
    softwares: Set<string>;
    fileFormat: 'xlsx' | 'xls';
};

export const exportToExcel = async ({
    selectedColsToExport,
    selectedDataToExport,
    filename,
    origin,
    sheetName = 'Sheet1',
    extraRows = [],
    extraSheets = [],
    softwares,
    fileFormat,
}: ExcelT): Promise<void> => {
    const headers = selectedColsToExport.map((col) => col.key);
    const headersTranslated = selectedColsToExport.map(
        (col) => col.label ?? translateBlockValue(col.key, false, softwares),
    );
    const { utils, writeFileXLSX, writeFile } = await import('xlsx');
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(selectedDataToExport, {
        header: headers,
        skipHeader: true,
        origin,
    });
    // Grand Angle requires specific formatting for the amount column.
    if (softwares.has('GRAND_ANGLE')) {
        const range = utils.decode_range(ws['!ref'] as string);
        const amountColIndex = headers.findIndex((colKey) =>
            colKey.endsWith('.amount'),
        );
        // SheetJS exposes an object like { s: { r: 0, c: 0 }, e: { r: 10, c: 10 } }
        // for the range, where s is the start and e is the end. We therefore start
        // from the first row after the header, which is range.s.r + 1.
        for (let row = range.s.r + 1; row <= range.e.r; ++row) {
            const ref = utils.encode_cell({ r: row, c: amountColIndex });
            if (ws[ref]) {
                // z is the cell format, t is the cell type (yes, enjoy)
                ws[ref].z = '#,##0.00\\ "€";\\-#,##0.00\\ "€"';
                ws[ref].t = 'n';
            }
        }
    }
    utils.sheet_add_aoa(ws, [...extraRows, headersTranslated]);
    utils.book_append_sheet(wb, ws, sheetName);

    extraSheets.forEach((sheet) => {
        const workingSheet = utils.aoa_to_sheet([['']]);
        utils.book_append_sheet(wb, workingSheet, sheet);
    });
    if (fileFormat === 'xlsx') {
        writeFileXLSX(wb, `${filename}.xlsx`);
    } else {
        // biff8 is the Excel 97-2004 Binary format
        // See https://docs.sheetjs.com/docs/miscellany/formats/#excel-97-2004-binary-biff8
        writeFile(wb, `${filename}.xls`, { bookType: 'biff8' });
    }
};
